<template>
  <ExpiButton class="mt-1"
              button-style="outline"
              :icon-classes="!generating ? 'fa fa-file-pdf-o' : ''"
              :is-busy="generating"
              :is-disabled="!active"
              :label-id="buttonLabel"
              :click="handleGeneratePdf" />
</template>

<script lang="ts">
import ExpiButton from '@/components/common/ExpiButton.vue'
import { IPerformance } from '@/components/interfaces'
import {
  useCabinStore,
  useCruiseStore,
  useDetailsStore,
  useFlexFlightStore,
  useFlightStore,
  usePriceStore,
  useRailStore,
  useUiStore,
  useUserStore,
} from '@/store'
import { useAppStore } from '@/store/app'
import { PDFButtonTrackingEventParams } from '@/store/tracking/interface'
import { IRequest } from '@gecko-akh/pkg-cruise-api-types/src/vivien/pdf_offer'
import { client as vivien } from '@gecko-akh/vivien/client'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { handleError, handleInfo } from '../../../general/errorHandling'
import { addTrackingEvent, buildPDFButtonTrackingEvent } from '../../../store/tracking/helper'

export default defineComponent({
  components: {
    ExpiButton
  },
  props: {
    active: { type: Boolean, default: true },
  },
  data: () => {
    return {
      generating: false,
    }
  },

  computed: {
    ...mapState(useAppStore, {
      stage: store => store.stage,
    }),
    ...mapState(useUserStore, {
      pdfWithAgencyInformation: store => store.pdfWithAgencyInformation,
    }),
    ...mapWritableState(useAppStore, {
      pdf: 'pdf',
    }),
    ...mapState(useDetailsStore, {
      mandatoryFlightForPriceModel: store => store.mandatoryFlightForPriceModel,
      paxConfig: store => store.paxConfig,
      priceModel: store => store.priceModel,
      transportation: store => store.transportation,
    }),
    ...mapState(useFlexFlightStore, {
      railDirectionFlexRNF: store => store.railDirection,
      railOptionFlexRNF: store => store.selectedRailOption,
      selectedFlexFlight: store => store.selectedFlight,
    }),
    ...mapState(useCruiseStore, {
      cruise: store => store.cruise,
      route: store => store.route,
      ship: store => store.ship,
    }),
    ...mapState(useCabinStore, {
      type: store => store.type,
      category: store => store.category,
      cabin: store => store.cabin,
    }),
    ...mapState(useFlightStore, {
      railDirectionRNF: store => store.railDirectionRNF,
      railOptionRNF: store => store.selectedRailOption,
      selectedAidaFlight: store => store.selectedFlight,
    }),
    ...mapState(useRailStore, {
      railOption: store => store.selectedRailOption,
      railDirection: store => store.railDirection,
    }),
    ...mapState(usePriceStore, {
      cabinTotal: store => store.cabinTotal,
      transportationTotal: store => store.transportationTotal,
      total: store => store.total,
    }),

    buttonLabel(): string {
      return this.generating ? 'Erzeuge PDF ...' : 'PDF erstellen'
    },
  },

  methods: {
    ...mapActions(useUiStore, {
      setDetailsPanelActiveTab: 'setDetailsPanelActiveTab',
    }),
    ...mapActions(useDetailsStore, {
      getOfferRequestForVivien: 'getOfferRequestForVivien'
    }),

    handleTracking() {
      const params: PDFButtonTrackingEventParams = {
        cruise: this.cruise,
        route: this.route,
        ship: this.ship,
        cabin: this.cabin,
        flight: this.selectedAidaFlight,
        category: this.category,
        totalPrice: this.total,
        mandatoryFlight: this.mandatoryFlightForPriceModel,
        cabinPrice: this.cabinTotal,
        paxConfig: this.paxConfig!,
        transportationTotal: this.transportationTotal,
        priceModel: this.priceModel,
        type: this.type,
        transportation: this.transportation,
        flexFlight: this.selectedFlexFlight,
        railOptionRNF: this.transportation === 'Flug' ? this.railOptionRNF : this.railOptionFlexRNF,
        railDirectionRNF: this.transportation === 'Flug' ? this.railDirectionRNF : this.railDirectionFlexRNF,
        railOption: this.railOption,
        railDirection: this.railDirection,
      }
      const event = buildPDFButtonTrackingEvent(params)
      addTrackingEvent(event, 'PDF Button Tracking Event')
    },

    async handleGeneratePdf() {
      try {
        await this.generatePdf()
      } catch (error) {
        handleError({ error, origin: 'handleGeneratePdf' })
        alert('Aktuell kann für diese Cruise leider kein PDF erzeugt werden.')
      }

      if (!this.generating) {
        this.handleTracking()
      }

      this.generating = false
    },

    async generatePdf() {
      if (!this.active) {
        return
      }

      this.generating = false

      const perf: IPerformance = {
        pdfTotalTime: performance.now(),
      }

      handleInfo('Generate PDF', {
        cruiseId: this.cruise?.id,
        pdfWithAgencyInformation: this.pdfWithAgencyInformation,
        transportationType: this.transportation,
      })

      await this.fetchPdfFromVivien(perf)
    },
    showPdf(pdf: Blob, title: string = 'unnamed') {
      if (this.stage === 'dev') {
        this.pdf = window.URL.createObjectURL(pdf)
        this.setDetailsPanelActiveTab('pdf')
      } else {
        const url = window.URL.createObjectURL(pdf)
        const anchor = document.createElement('a')
        anchor.style.display = 'none'
        anchor.href = url
        anchor.download = title
        document.body.appendChild(anchor)
        anchor.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(anchor)
      }
    },
    async fetchPdfFromVivien(perf: IPerformance) {
      this.generating = true
      const offerRequest: IRequest = this.getOfferRequestForVivien()
      const pdfResponse = await vivien.pdfOffer(offerRequest)
      const pdfDataResponse = await fetch(pdfResponse.file)
      const pdf = await pdfDataResponse.blob()

      this.showPdf(pdf, pdfResponse.title)

      perf.pdfTotalTime = performance.now() - perf.pdfTotalTime
      this.generating = false
    },

    getTranslateText: (label: string) => {
      return label
    }
  },
})
</script>